import { TCT_DRAFT } from "./fixtures";
import type { TctScenario, TctScenarioCategory } from "./types";

export const TCT_CATEGORY_LABELS: Readonly<
  Record<TctScenarioCategory, string>
> = {
  discovery: "Discovery",
  identity: "Identity representation",
  revalidation: "Conditional reuse",
  representation: "Selected representations",
  delivery_failure: "Delivery-path failures",
};

export const TCT_SCENARIOS: readonly TctScenario[] = [
  {
    id: "catalog-discovery",
    title: "A generic index link becomes a confirmed catalog",
    category: "discovery",
    kind: "catalog_discovery",
    summary:
      "The entry response advertises a candidate JSON index. The client retrieves it and confirms TCT from its exact profile, version, and structure.",
    rule:
      "The index relation is generic. Discovery produces a candidate, never proof, and clients do not guess a conventional path.",
    references: [
      { label: "TCT §4.1", href: `${TCT_DRAFT}#section-4.1` },
      { label: "TCT §7", href: `${TCT_DRAFT}#section-7` },
    ],
  },
  {
    id: "identity-murl",
    title: "Identity bytes bind the M-URL validator",
    category: "identity",
    kind: "identity_murl",
    summary:
      "An identity request selects exact JCS UTF-8 bytes with the corresponding strong SHA-256 ETag and recommended Content-Digest.",
    rule:
      "The body bytes, selected-representation ETag, and digest metadata must agree. The ETag remains an ordinary representation validator.",
    references: [
      { label: "TCT §5.1", href: `${TCT_DRAFT}#section-5.1` },
      { label: "TCT §6", href: `${TCT_DRAFT}#section-6` },
    ],
  },
  {
    id: "conditional-304",
    title: "The identity representation revalidates to 304",
    category: "revalidation",
    kind: "conditional_304",
    summary:
      "The client quotes the catalog hint, requests identity, and receives a matching 304 carrying the current M-URL ETag.",
    rule:
      "A catalog hint can construct If-None-Match only after syntax and origin checks; the selected M-URL response remains authoritative.",
    references: [
      { label: "TCT §9.2", href: `${TCT_DRAFT}#section-9.2` },
      { label: "TCT §9.3", href: `${TCT_DRAFT}#section-9.3` },
    ],
  },
  {
    id: "catalog-hint-reuse",
    title: "A fresh catalog can bound cached reuse",
    category: "revalidation",
    kind: "catalog_reuse",
    summary:
      "A revalidated M-Sitemap repeats the cached identity M-URL ETag hint, so the client elects to reuse its cached M-URL without another request.",
    rule:
      "This optimization inherits catalog freshness and publication races. It does not prove later target-resource freshness.",
    references: [
      { label: "TCT §7.4", href: `${TCT_DRAFT}#section-7.4` },
    ],
  },
  {
    id: "bidirectional-links",
    title: "Alternate out; canonical back",
    category: "discovery",
    kind: "bidirectional_links",
    summary:
      "The C-URL advertises its machine-facing alternate and the confirmed M-URL links back to the canonical human-facing resource.",
    rule:
      "Neither link alone proves TCT. The client still validates the M-URL profile and representation.",
    references: [
      { label: "TCT §4.2", href: `${TCT_DRAFT}#section-4.2` },
      { label: "TCT §4.3", href: `${TCT_DRAFT}#section-4.3` },
    ],
  },
  {
    id: "coded-variant",
    title: "A gzip variant owns a different strong ETag",
    category: "representation",
    kind: "coded_variant",
    summary:
      "A request that permits gzip selects different representation data with its own strong ETag and Vary metadata.",
    rule:
      "Draft-03 permits coded variants. The catalog hint still refers to identity and cannot be reused as the gzip validator.",
    references: [
      { label: "TCT §6.3", href: `${TCT_DRAFT}#section-6.3` },
      { label: "TCT §10", href: `${TCT_DRAFT}#section-10` },
    ],
  },
  {
    id: "weak-identity-etag",
    title: "A weak identity ETag fails the profile",
    category: "delivery_failure",
    kind: "weak_identity_etag",
    summary:
      "The body is canonical, but the public response weakens the body-derived validator with W/.",
    rule:
      "Every identity TCT JSON representation requires the strong Draft-03 SHA-256 ETag form.",
    references: [
      { label: "TCT §6.2", href: `${TCT_DRAFT}#section-6.2` },
    ],
  },
  {
    id: "coded-etag-reuse",
    title: "Different gzip bytes cannot reuse the identity ETag",
    category: "delivery_failure",
    kind: "coded_etag_reuse",
    summary:
      "An intermediary serves coded bytes but leaves the identity representation's strong ETag attached.",
    rule:
      "Non-identical coded and identity representation data require different validators and correct Vary handling.",
    references: [
      { label: "TCT §6.3", href: `${TCT_DRAFT}#section-6.3` },
      { label: "TCT §10", href: `${TCT_DRAFT}#section-10` },
    ],
  },
  {
    id: "missing-murl-links",
    title: "Shape alone does not establish an M-URL",
    category: "delivery_failure",
    kind: "missing_murl_links",
    summary:
      "A JSON envelope resembles TCT but the response omits the required profile and canonical links.",
    rule:
      "Clients confirm both the exact profile and the reverse canonical mapping instead of accepting a familiar JSON shape.",
    references: [
      { label: "TCT §4.3", href: `${TCT_DRAFT}#section-4.3` },
      { label: "TCT §5.1", href: `${TCT_DRAFT}#section-5.1` },
    ],
  },
  {
    id: "cross-origin-hint",
    title: "A catalog hint cannot cross origins",
    category: "delivery_failure",
    kind: "cross_origin_hint",
    summary:
      "A catalog item points at another origin while carrying an identity ETag hint learned from example.com.",
    rule:
      "The client must not construct a conditional request at another origin from that hint. Cross-origin traversal needs separate security policy.",
    references: [
      { label: "TCT §9.1", href: `${TCT_DRAFT}#section-9.1` },
      { label: "TCT §12.3", href: `${TCT_DRAFT}#section-12.3` },
    ],
  },
  {
    id: "intermediary-rewrite",
    title: "Rewritten JSON needs a corresponding validator",
    category: "delivery_failure",
    kind: "intermediary_rewrite",
    summary:
      "The origin publishes the certified JCS identity, but the observed public body has an added byte while retaining the old ETag.",
    rule:
      "A cache or CDN must not rewrite TCT JSON without generating the corresponding validator for the selected representation.",
    references: [
      { label: "TCT §6.1", href: `${TCT_DRAFT}#section-6.1` },
      { label: "TCT §10", href: `${TCT_DRAFT}#section-10` },
    ],
  },
];

export function findTctScenario(id: string): TctScenario | undefined {
  return TCT_SCENARIOS.find((scenario) => scenario.id === id);
}
